import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { api, ApiError, terminalsApi, type LiteTrackResult, type TerminalOption } from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Spinner } from '../components/ui';

function Row({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

export function TrackingPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { token, user } = useAuth();
  const fmtDate = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(i18n.language);
  };
  /** Datum + Uhrzeit (für Meilenstein-Zeitstempel wie Löschung/Gate-out/Leerabgabe). */
  const fmtDateTime = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' });
  };
  const isAdmin = user?.role === 'org_admin' || user?.role === 'super_admin';
  // Prefill `?number=` (EPIC Q: „Neue Abfrage" aus dem Archiv-Reiter — Reaktivierung = neue Reise).
  const [searchParams] = useSearchParams();
  const [containerNumber, setContainerNumber] = useState(searchParams.get('number') ?? '');
  const [carrierCode, setCarrierCode] = useState('');
  const [result, setResult] = useState<LiteTrackResult | null>(null);
  const [error, setError] = useState('');
  /** Gesetzt, wenn der Lite-Flow per 402 gesperrt ist (Trial/Limit) — zeigt Upgrade-Hinweis. */
  const [gate, setGate] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Optionales Ziel-Terminal (H6): gibt der User es vor, ist keine Reederei nötig. */
  const [terminals, setTerminals] = useState<TerminalOption[]>([]);
  const [terminalId, setTerminalId] = useState('');
  const [terminalQuery, setTerminalQuery] = useState('');
  const [terminalOpen, setTerminalOpen] = useState(false);

  useEffect(() => {
    if (!token) return;
    terminalsApi
      .list(token)
      .then(setTerminals)
      .catch(() => setTerminals([]));
  }, [token]);

  const terminalMatches = terminals.filter((tm) =>
    tm.label.toLowerCase().includes(terminalQuery.trim().toLowerCase()),
  );
  function selectTerminal(tm: TerminalOption): void {
    setTerminalId(tm.id);
    setTerminalQuery(tm.label);
    setTerminalOpen(false);
  }
  function clearTerminal(): void {
    setTerminalId('');
    setTerminalQuery('');
    setTerminalOpen(false);
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError('');
    setGate(null);
    setResult(null);
    setBusy(true);
    try {
      setResult(
        await api.post<LiteTrackResult>(
          '/lite/track',
          { containerNumber, carrierCode: carrierCode || undefined, terminal: terminalId || undefined },
          token,
        ),
      );
    } catch (err) {
      if (err instanceof ApiError && err.httpStatus === 402) {
        setGate(err.code ?? 'trial_exhausted');
      } else {
        setError(err instanceof ApiError ? err.message : 'Error');
      }
    } finally {
      setBusy(false);
    }
  }

  const found = result?.result?.status === 'found' ? result.result.data : null;
  const terminalMerge = (found?.raw?.terminalMerge ?? undefined) as
    | { carrierEta?: string | null; carrierEtd?: string | null }
    | undefined;

  /** Datumswert mit Quellen-Badge (Terminal/Reederei) + Reederei-Wert als Mouseover (H5/BR-5). */
  function sourceValue(iso: string, source: string | undefined, carrierIso?: string | null): JSX.Element {
    const isTerminal = source === 'terminal';
    const title = isTerminal && carrierIso ? `${t('lite.source.carrier')}: ${fmtDate(carrierIso)}` : undefined;
    return (
      <span className="inline-flex items-center gap-2" title={title}>
        {fmtDate(iso)}
        {source && (
          <Badge variant={isTerminal ? 'success' : 'default'} className="cursor-default">
            {t(`lite.source.${source}`, { defaultValue: source })}
          </Badge>
        )}
      </span>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('lite.title')}</h1>
        <p className="text-muted-foreground">{t('lite.hint')}</p>
      </div>

      <Card className="max-w-xl">
        <CardContent className="p-5">
          <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
              <div>
                <Label htmlFor="cn">{t('lite.containerNumber')}</Label>
                <Input
                  id="cn"
                  value={containerNumber}
                  placeholder={t('lite.containerPlaceholder')}
                  onChange={(e) => setContainerNumber(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="cc">{t('lite.carrier')}</Label>
                <Input id="cc" value={carrierCode} placeholder="MSCU" onChange={(e) => setCarrierCode(e.target.value)} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t('lite.carrierHint')}</p>

            <div className="relative">
              <Label htmlFor="terminal">{t('lite.terminalSelect')}</Label>
              <div className="relative">
                <Input
                  id="terminal"
                  className="pr-9"
                  value={terminalQuery}
                  placeholder={t('lite.terminalPlaceholder')}
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={terminalOpen}
                  onFocus={() => setTerminalOpen(true)}
                  onChange={(e) => {
                    setTerminalQuery(e.target.value);
                    setTerminalId('');
                    setTerminalOpen(true);
                  }}
                  onBlur={() => window.setTimeout(() => setTerminalOpen(false), 120)}
                />
                {(terminalId || terminalQuery) && (
                  <button
                    type="button"
                    onClick={clearTerminal}
                    aria-label={t('lite.terminalClear')}
                    className="absolute inset-y-0 right-2 my-auto flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                  >
                    ×
                  </button>
                )}
                {terminalOpen && terminalMatches.length > 0 && (
                  <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border bg-background py-1 shadow-md">
                    {terminalMatches.map((tm) => (
                      <li key={tm.id}>
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            selectTerminal(tm);
                          }}
                          className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-muted"
                        >
                          <span>{tm.label}</span>
                          <span className="text-xs text-muted-foreground">{tm.port}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{t('lite.terminalHint')}</p>
            </div>

            <Button type="submit" disabled={busy}>
              {busy ? <Spinner /> : t('lite.track')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {error && <Alert kind="error">{error}</Alert>}
      {gate && (
        <Alert kind="warn">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{gate === 'usage_limit_reached' ? t('lite.limitReached') : t('lite.trialExhausted')}</span>
            {isAdmin ? (
              <Link to="/billing">
                <Button size="sm" variant="secondary">
                  {t('lite.goToBilling')}
                </Button>
              </Link>
            ) : (
              <span className="text-xs text-muted-foreground">{t('lite.askAdmin')}</span>
            )}
          </div>
        </Alert>
      )}
      {result?.needsCarrierInput && <Alert kind="warn">{t('lite.needsCarrier')}</Alert>}

      {result && !result.needsCarrierInput && (
        <Card>
          <CardHeader>
            <CardTitle>
              {t('lite.result')}: {result.containerNumber}
              {(found?.carrierName ?? result.carrierCode) && (
                <span className="font-normal text-muted-foreground">
                  {' '}· {t('lite.resolvedAs')} {found?.carrierName ?? result.carrierCode}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {result.terminalMismatch && (
              <Alert kind="warn">
                {t('lite.terminalMismatch', {
                  actual: result.terminalMismatch.actual,
                  selected: result.terminalMismatch.selectedLabel,
                })}
              </Alert>
            )}
            {result.result?.status === 'not_found' && (
              <p className="text-muted-foreground">{terminalId ? t('lite.notFoundTerminal') : t('lite.notFound')}</p>
            )}
            {result.result?.status === 'error' && (
              <Alert kind="error">
                {t('lite.errorSource')}: {result.result.message}
              </Alert>
            )}
            {found && (
              <>
                {(found.terminal || found.terminalState) && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="primary">
                      {t('lite.terminalKnown')}
                      {found.terminal ? `: ${found.terminal}` : ''}
                    </Badge>
                    {found.terminalState && (
                      <Badge variant="warning">
                        {t(`lite.terminalState.${found.terminalState}`, { defaultValue: found.terminalState })}
                      </Badge>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <StatusBadge label={t('lite.fields.discharge')} flag={found.discharge} note={found.notes?.discharge} />
                  <StatusBadge label={t('lite.fields.release')} flag={found.release} note={found.notes?.release} />
                  <StatusBadge label={t('lite.fields.customs')} flag={found.customs} note={found.notes?.customs} />
                  <StatusBadge label={t('lite.fields.block')} flag={found.block} note={found.notes?.block} />
                  <StatusBadge label={t('lite.fields.pickup')} flag={found.pickup} note={found.notes?.pickup} />
                </div>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-8 gap-y-1.5 text-sm">
                  {(found.sizeType || found.isoType) && (
                    <Row label={t('lite.fields.size')}>
                      <span
                        title={found.isoType ? `ISO ${found.isoType}` : undefined}
                        className={found.isoType ? 'cursor-help underline decoration-dotted underline-offset-2' : undefined}
                      >
                        {found.sizeType ?? found.isoType}
                      </span>
                    </Row>
                  )}
                  {found.vessel && (
                    <Row label={t('lite.vessel')}>
                      {found.vessel}
                      {found.vesselImo && (
                        <span className="text-muted-foreground">
                          {' '}· {t('lite.imo')} {found.vesselImo}
                        </span>
                      )}
                      {found.transitVessels.length > 0 && (
                        <span className="text-muted-foreground">
                          {' '}· {t('lite.transit')}: {found.transitVessels.map((v) => v.name).join(', ')}
                        </span>
                      )}
                    </Row>
                  )}
                  {(found.originPort || found.destinationPort) && (
                    <Row label={t('lite.route')}>
                      {found.originPort ?? '—'}
                      <span className="text-muted-foreground"> → </span>
                      {found.destinationPort ?? '—'}
                    </Row>
                  )}
                  {found.port && <Row label={t('lite.port')}>{found.port}</Row>}
                  {found.terminal && <Row label={t('lite.terminal')}>{found.terminal}</Row>}
                  {found.eta && (
                    <Row label={t('lite.eta')}>{sourceValue(found.eta, found.etaSource, terminalMerge?.carrierEta)}</Row>
                  )}
                  {found.etd && (
                    <Row label={t('lite.etd')}>{sourceValue(found.etd, found.etdSource, terminalMerge?.carrierEtd)}</Row>
                  )}
                  {found.dischargedAt && <Row label={t('lite.dischargedAt')}>{fmtDateTime(found.dischargedAt)}</Row>}
                  {found.gateOutAt && <Row label={t('lite.gateOutAt')}>{fmtDateTime(found.gateOutAt)}</Row>}
                  {found.emptyReturnedAt && (
                    <Row label={t('lite.emptyReturnedAt')}>{fmtDateTime(found.emptyReturnedAt)}</Row>
                  )}
                </dl>
                {found.timeline && found.timeline.length > 0 && (
                  <div className="space-y-2 border-t pt-4">
                    <h4 className="text-sm font-semibold">{t('timeline.heading')}</h4>
                    <ul className="space-y-1.5">
                      {found.timeline.map((ev, i) => (
                        <li key={`${ev.event_key}-${i}`} className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="w-24 shrink-0 text-muted-foreground">{fmtDate(ev.occurred_at)}</span>
                          <span className="flex-1 min-w-[12rem]">
                            {t(`timeline.events.${ev.event_key}`, { defaultValue: ev.event_key })}
                            {ev.location && (
                              <span className="ml-1 font-medium text-foreground">· {ev.location}</span>
                            )}
                          </span>
                          {ev.is_estimated && (
                            <Badge className="bg-foreground/80 text-background">{t('timeline.estimated')}</Badge>
                          )}
                          {ev.sources.map((s, j) => (
                            <Badge key={`${s.badge}-${j}`} variant="default">
                              {t(`timeline.source.${s.badge}`, { defaultValue: s.badge })}
                            </Badge>
                          ))}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
